import { Button, DialogTrigger } from '@ibsys/kit';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ServerSearch } from '@/shared/hooks';
import { getOutsideTotal } from '@/shared/lib';
import { SelectionDialog, useLocalSelectionItems, useSelectionDialogState } from '@/shared/ui';

type Labels = {
  addButton: string;
  dialogTitle: string;
  searchPlaceholder?: string;
  noItemsFound?: string;
  apply?: string;
};

type AddNestedItemsDialogProps<T> = {
  /** Все записи, из которых собирается состав */
  allItems: T[];
  /** Сколько записей в справочнике всего, если `allItems` — лишь первое окно его выдачи */
  total?: number;
  /** Записи `allItems` загружаются */
  isLoading?: boolean;
  /** Текущий состав: с ним окно открывается отмеченным */
  value: T[];
  /** Новый состав — отмеченные записи */
  onSave: (items: T[]) => void;
  labels: Labels;
  renderTitle: (item: T) => ReactNode;
  renderHint?: (item: T) => ReactNode;
  renderDescription?: (item: T) => ReactNode;
  getItemId: (item: T) => string;
  searchFilter?: (item: T, searchQuery: string) => boolean;
  /** Строка поиска потребителя, если выдачу `allItems` под неё набирает запрос на сервере */
  serverSearch?: ServerSearch;
};

/**
 * Кнопка пополнения вложенного списка и окно выбора его состава: окно открывается с текущим составом
 * отмеченным, подтверждение заменяет состав отмеченными записями — и добавляет, и исключает.
 */
export const AddNestedItemsDialog = <T,>({
  allItems,
  total,
  isLoading,
  value,
  onSave,
  labels,
  renderTitle,
  renderHint,
  renderDescription,
  getItemId,
  searchFilter,
  serverSearch,
}: AddNestedItemsDialogProps<T>) => {
  const { t } = useTranslation('common');
  // Отмеченные строки окна берутся из справочника, как и прочие: запись состава может нести не все
  // поля записи (описание). Справочник, пришедший после открытия окна, подменяет их на отрисовке
  const getInitialSelected = () => {
    const itemsById = new Map(allItems.map((item) => [getItemId(item), item]));
    return value.map((item) => itemsById.get(getItemId(item)) ?? item);
  };
  const dialogState = useSelectionDialogState({ getItemId, getInitialSelected });
  const { isOpen, selected, isSelectionChanged, handleToggle } = dialogState;
  // С поиском на сервере поле окна ведёт строку потребителя: по ней тот и набирает выдачу
  const search = serverSearch?.value ?? dialogState.search;
  const setSearch = serverSearch?.onChange ?? dialogState.setSearch;

  const handleOpenChange = (open: boolean) => {
    // Закрытие отказывается от набранного запроса — и от строки потребителя, иначе следующее
    // открытие показало бы выдачу под прежний запрос
    if (!open) serverSearch?.onChange('');
    dialogState.handleOpenChange(open);
  };

  // Состав в окне отмечен, а не исключён: выдача — справочник целиком под набранный запрос
  const filteredItems = useLocalSelectionItems({ items: allItems, getItemId, search, matchesSearch: searchFilter });

  const handleConfirm = () => {
    onSave(selected);
    handleOpenChange(false);
  };

  return (
    <SelectionDialog<T>
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm" className="ml-auto">
            {labels.addButton}
          </Button>
        </DialogTrigger>
      }
      title={labels.dialogTitle}
      search={search}
      onSearchChange={setSearch}
      items={filteredItems}
      // Состав в окне не исключён, поэтому из общего числа ничего не вычитается
      total={getOutsideTotal(total, allItems.length, 0)}
      isLoading={isLoading}
      selected={selected}
      onToggle={handleToggle}
      getItemId={getItemId}
      renderTitle={renderTitle}
      renderHint={renderHint}
      renderDescription={renderDescription}
      labels={{
        searchPlaceholder: labels.searchPlaceholder,
        empty: t('noItemsToSelect'),
        notFound: labels.noItemsFound,
        confirm: labels.apply ?? t('accept'),
      }}
      onConfirm={handleConfirm}
      confirmDisabled={!isSelectionChanged}
    />
  );
};
